import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'

type BreadcrumbLabels = Record<string, string>

interface BreadcrumbLabelsContextValue {
  labels: BreadcrumbLabels
  setLabel: (segment: string, label: string) => void
  removeLabel: (segment: string) => void
}

const BreadcrumbLabelsContext = createContext<BreadcrumbLabelsContextValue | undefined>(undefined)

/**
 * Provides a registry that maps URL path segments (e.g. a dynamic id) to
 * human-friendly breadcrumb labels. Features register a label for a segment
 * via {@link useSetBreadcrumbLabel}; the breadcrumb reads them via
 * {@link useBreadcrumbLabels}.
 */
export const BreadcrumbLabelsProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [labels, setLabels] = useState<BreadcrumbLabels>({})

  const setLabel = useCallback((segment: string, label: string) => {
    setLabels((current) => {
      if (current[segment] === label) {
        return current
      }
      return {...current, [segment]: label}
    })
  }, [])

  const removeLabel = useCallback((segment: string) => {
    setLabels((current) => {
      if (!(segment in current)) {
        return current
      }
      const next = {...current}
      delete next[segment]
      return next
    })
  }, [])

  const value = useMemo(() => ({labels, setLabel, removeLabel}), [labels, setLabel, removeLabel])

  return (
    <BreadcrumbLabelsContext.Provider value={value}>{children}</BreadcrumbLabelsContext.Provider>
  )
}

/**
 * Returns the map of registered breadcrumb labels keyed by URL path segment.
 * Returns an empty map when used outside a {@link BreadcrumbLabelsProvider}.
 */
export const useBreadcrumbLabels = (): BreadcrumbLabels => {
  const context = useContext(BreadcrumbLabelsContext)
  return context?.labels ?? {}
}

/**
 * Registers a human-friendly label for a URL path segment for as long as the
 * calling component is mounted. The label is removed automatically on unmount
 * or when the segment changes. No-op outside a {@link BreadcrumbLabelsProvider}.
 */
export const useSetBreadcrumbLabel = (
  segment: string | undefined,
  label: string | undefined
): void => {
  const context = useContext(BreadcrumbLabelsContext)
  const setLabel = context?.setLabel
  const removeLabel = context?.removeLabel

  useLayoutEffect(() => {
    if (!segment || !label || !setLabel || !removeLabel) {
      return
    }
    setLabel(segment, label)
    return () => removeLabel(segment)
  }, [segment, label, setLabel, removeLabel])
}
